// Where a `?return_to=` sends someone when they are done here.
//
// An app links to a plugin's setup page with its own URL as `return_to`, and
// that URL is usually on ANOTHER ORIGIN (an app on its own domain linking to
// Paradigm). react-router's navigate() only moves within this app, so handing it
// an absolute URL produced a broken in-app path instead of a way back.
//
// THE VALUE IS ATTACKER-CHOSEN — anyone can craft the link. So:
//   - only http(s): `javascript:` handed to location.assign would run here;
//   - backslashes are slashes: browsers read `\\evil.com` and `/\evil.com` as
//     `//evil.com`, a host, and so does this, rather than hoping they agree;
//   - same origin stays in the router; ANY other origin is never navigated to
//     automatically. It comes back as `{ external, host }` and the page asks
//     first (`leavingFor`) — an open redirect is a link that sends someone
//     somewhere they did not choose, and a button naming the host is a choice.

export function resolveReturnTo(returnTo, origin = window.location.origin) {
  if (!returnTo || typeof returnTo !== 'string') return null;
  let u;
  try {
    u = new URL(returnTo.trim().replace(/\\/g, '/'), origin);
  } catch {
    return null;
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  if (u.origin === origin) return { internal: u.pathname + u.search + u.hash };
  return { external: u.href, host: u.host };
}

// Moves within the app only. Returns whether it navigated: an external target
// is left to the page, which shows `leavingFor` and lets the person choose.
export function goReturnTo(target, navigate) {
  if (!target || !target.internal) return false;
  navigate(target.internal);
  return true;
}

// The host a target would take someone to outside ofSelf, or null when it
// stays here — what the "Leave ofSelf for …?" confirmation names.
export function leavingFor(target) {
  return target && target.external ? target.host || null : null;
}

// Leaves for another origin, once the person has chosen to (`leavingFor`).
export function leaveTo(url) {
  window.location.assign(url);
}

// The query suffix that carries `return_to` through a round trip (the plugin's
// setup → its provider → back), so connecting does not strand someone here.
export function withReturnTo(path, returnTo) {
  if (!returnTo) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}return_to=${encodeURIComponent(returnTo)}`;
}

// Where a `consent_url` from the API should take someone, as an href.
//
// The API now answers with an ABSOLUTE url on the platform's host, because an
// app on another origin cannot resolve a bare path. Here, on that host, the
// same-origin url collapses back to an in-app path so following it stays in the
// router; anything else is returned whole. Unusable values give null.
export function consentHref(consentUrl, origin = window.location.origin) {
  const t = resolveReturnTo(consentUrl, origin);
  if (!t) return null;
  return t.internal || t.external;
}
